import { Injectable } from '@angular/core';

import { filter, Observable, take } from 'rxjs';
import { io, Socket } from 'socket.io-client';

import { AuthService, TabIdentityService } from '@tablesready/frontend-auth';
import { BaseSocketService } from './base-socket.service';
import { LoggerService } from '@tablesready/frontend-logger';
import { IStatus, StatusService } from '@tablesready/frontend-status-bar';
import { IAlertTypes } from '@tablesready/frontend-alerts-bar';
import { Capacitor } from '@capacitor/core';
import { getBackEndInstance } from '@tablesready/frontend-get-backend';

declare const process: {
  env: {
    LOCAL_IP: string;
    PORT: string;
    NODE_ENV: string;
    NATIVE_APP_HOST_URL: string;
    SOCKET_PORT: string;
  };
};

@Injectable({ providedIn: 'root' })
export class SocketService extends BaseSocketService {
  constructor(
    private readonly authService: AuthService,
    loggerService: LoggerService,
    private readonly statusService: StatusService,
    private readonly tabIdentity: TabIdentityService,
  ) {
    super(loggerService);
    // A locked tab must stop receiving the other login's live updates.
    this.tabIdentity.lock$.pipe(filter(Boolean), take(1)).subscribe(() => {
      this.disconnect();
    });
  }

  override connect(): void {
    if (this.tabIdentity.isLocked()) {
      return;
    }

    super.connect();
  }

  protected createSocket(): Observable<Socket> {
    return new Observable<Socket>((observer) => {
      const socket = io(this.getBackendUrl(), {
        transports: ['websocket'],
        forceNew: true,
        reconnection: true,
        reconnectionDelay: Capacitor.isNativePlatform() ? 1000 : 500,
        reconnectionDelayMax: 5000,
        // Keep retrying so a long tab freeze never permanently kills the socket; auth rejects are
        // server-initiated disconnects that do not auto-retry, so this does not hammer the server.
        reconnectionAttempts: Capacitor.isNativePlatform() ? 10000 : Infinity,
        // Callback form is re-evaluated on every (re)connection attempt, so reconnects always send
        // the current token (the captured value can be stale after a long idle).
        auth: (callback) => {
          const token = this.authService.getToken();
          // A reconnect must not authenticate this tab as a login another tab switched to.
          if (token && !this.tabIdentity.verifyToken(token, 'request')) {
            callback({ token: '' });

            return;
          }

          callback({ token: `Bearer ${token}` });
        },
      });

      this.registerLifecycleHandlers(socket, observer);

      // TODO: move is outside, e.g. NewsService?
      socket.on('news', (data) => {
        const statusObj: IStatus = {
          name: 'news',
          type: IAlertTypes.warning,
          id: 'news',
          dismissable: false,
          body: data,
        };

        if (data === 'clear') {
          this.statusService.clearStatusById(statusObj.id);
        } else {
          this.statusService.add(statusObj);
        }
      });

      return this.socketTeardown(socket);
    });
  }

  /**
   * Never dial without a usable access token — the backend rejects `Bearer undefined` with
   * `unauthorized` + a non-retrying disconnect. Callers refresh first (ensureActiveTokenFresh).
   */
  protected override canReconnect(): boolean {
    return !this.tabIdentity.isLocked() && Boolean(this.authService.getToken());
  }

  private getBackendUrl(): string {
    if (process.env.SOCKET_PORT) {
      console.log('testing sockets through proxy');
      return `http://${process.env.LOCAL_IP}:${process.env.SOCKET_PORT}`;
    }

    if (
      Capacitor.isNativePlatform() &&
      process.env.NODE_ENV === 'production' &&
      process.env.NATIVE_APP_HOST_URL &&
      process.env.NATIVE_APP_HOST_URL !== ''
    ) {
      return process.env.NATIVE_APP_HOST_URL;
    }

    if (process.env.NODE_ENV === 'development' && process.env.LOCAL_IP) {
      return `http://${process.env.LOCAL_IP}:${process.env.PORT}`;
    }

    return getBackEndInstance();
  }
}
